/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ChangedEvent, hasValue, ObservableMap, toArray } from '@typescript-kit/core';
import { ViewService } from '@angular-kit/view';
import {
  Alignments,
  ContainerModel,
  FormFieldModel,
  Orientations,
  TextInputModel,
  TextModel,
  ValueData,
  ValueModel
} from '@typescript-kit/view';
import { TimeRecordingViewModel } from '../view-model/time-recording.view-model';
import { TimeRecordingService } from '../../../../../teamworks/global/src/time-recording/service/time-recording.service';

export class EmployeeTimeRecordingSummaryViewService extends ViewService<TimeRecordingViewModel, ContainerModel> {

  initialize(viewModel?: TimeRecordingViewModel): void {
    super.initialize(viewModel);
  }

  finalize(): void {
    super.finalize();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    const changes = event.changes;
    if (changes['summary']) {
      this.refreshComponentModel(this.componentModel);
    }
  }

  protected createComponentModel(): ContainerModel {
    const componentModel = new ContainerModel({
      items: new ObservableMap({
        'carryover': this.createCarryoverModel(),
        'workingTime': this.createWorkingTimeModel(),
        'flexTime': this.createFlexTimeModel(),
        'holiday': this.createHolidayModel(),
        'projects': this.createProjectSumModel()
      })
    });
    this.refreshComponentModel(componentModel);
    return componentModel;
  }

  protected refreshComponentModel(model: ContainerModel) {
    if (!this.viewModel.employeeId) {
      model.isHidden = true;
      return;
    }
    model.isHidden = false;
    model.tags.set('app-print-view', this.viewModel.printView);
    model.orientation = this.viewModel.printView ? Orientations.SIDE_BY_SIDE : Orientations.STACKED;
    this.refreshCarryoverModel(model.items.get('carryover') as ContainerModel);
    this.refreshWorkingTimeModel(model.items.get('workingTime') as ContainerModel);
    this.refreshFlexTimeModel(model.items.get('flexTime') as ContainerModel);
    this.refreshHolidayModel(model.items.get('holiday') as ContainerModel);
    this.refreshProjectSumModel(model.items.get('projects') as ContainerModel);
  }

  private createCarryoverModel(): ContainerModel {
    return new ContainerModel({
      items: new ObservableMap({
        'carryoverTitle': new TextModel({
          tags: ['app-carryover', 'app-title'],
          text: 'time-recording/view/time-recording-summary/carryover'
        }),
        'estimatedFlextimeCarryover': new FormFieldModel({
          key: 'estimatedFlextimeCarryover', tags: ['app-flextime-carryover'],
          label: 'time-recording/view/time-recording-summary/flextime-proposal',
          control: this.createValueModel()
        }),
        'flextimeCarryover': new FormFieldModel({
          key: 'flextimeCarryover', tags: ['app-flextime-carryover'],
          label: 'time-recording/view/time-recording-summary/flextime',
          control: new TextInputModel({
            // format: { key: 'number', options: { nullFormat: '' } }, suffix: ' h', alignment: Alignments.RIGHT,
            format: 'number-2', suffix: ' h', alignment: Alignments.RIGHT,
            onValueChanged: (value: number) => {
              if (!this.viewModel.carryover) {
                return;
              }
              this.viewModel.carryover.flextimeBalance.value = value;
            }
          })
        }),
        'estimatedHolidayCarryover': new FormFieldModel({
          key: 'estimatedHolidayCarryover', tags: ['app-holiday-carryover'],
          label: 'time-recording/view/time-recording-summary/holiday-proposal',
          control: this.createValueModel()
        }),
        'holidayCarryover': new FormFieldModel({
          key: 'holidayCarryover', tags: ['app-holiday-carryover'],
          label: 'time-recording/view/time-recording-summary/holiday',
          control: new TextInputModel({
            format: 'number-2', suffix: ' h', alignment: Alignments.RIGHT,
            onValueChanged: (value: number) => {
              if (!this.viewModel.carryover) {
                return;
              }
              this.viewModel.carryover.holidayBalance.value = value;
            }
          })
        })
      })
    });
  }

  private refreshCarryoverModel(model: ContainerModel) {
    if (this.viewModel.printView) {
      model.isHidden = true;
      return;
    }
    const carryover = this.viewModel.carryover;
    (model.items.get('estimatedFlextimeCarryover') as FormFieldModel).control.value = carryover ? carryover.workProposal : null;
    (model.items.get('flextimeCarryover') as FormFieldModel).control.value = carryover ? carryover.flextimeBalance.value : null;
    (model.items.get('estimatedHolidayCarryover') as FormFieldModel).control.value = carryover ? carryover.holidayProposal : null;
    (model.items.get('holidayCarryover') as FormFieldModel).value = carryover ? carryover.holidayBalance.value : null;
  }

  private createWorkingTimeModel(): ContainerModel {
    return new ContainerModel({
      items: new ObservableMap({
        'workingTimeTitle': new TextModel({
          tags: ['app-working-time', 'app-title'],
          text: 'time-recording/view/time-recording-summary/working-time'
        }),
        'totalWorkHours': new FormFieldModel({
          key: 'totalWorkHours', tags: ['app-work-hours'],
          label: 'time-recording/view/time-recording-summary/work',
          control: this.createValueModel()
        }),
        'totalPublicHolidayHours': new FormFieldModel({
          key: 'totalPublicHolidayHours', tags: ['app-public-holiday-hours'],
          label: 'time-recording/view/time-recording-summary/public-holiday',
          control: this.createValueModel()
        }),
        'totalSickHours': new FormFieldModel({
          key: 'totalSickHours', tags: ['app-sick-hours'],
          label: 'time-recording/view/time-recording-summary/sick',
          control: this.createValueModel()
        }),
        'totalShortTimeWorkHours': new FormFieldModel({
          key: 'totalShortTimeWorkHours', tags: ['app-short-time-work-hours'],
          label: 'time-recording/view/time-recording-summary/short-time-work',
          control: this.createValueModel()
        }),
        'totalHolidayHours': new FormFieldModel({
          key: 'totalHolidayHours', tags: ['app-holiday-hours'],
          label: 'time-recording/view/time-recording-summary/holiday',
          control: this.createValueModel()
        }),
        'totalCompTimeHours': new FormFieldModel({
          key: 'totalCompTimeHours', tags: ['app-comp-time-hours'],
          label: 'time-recording/view/time-recording-summary/comp-time',
          control: this.createValueModel()
        }),
        'totalActualHours': new FormFieldModel({
          key: 'totalActualHours', tags: ['app-actual-hours'],
          label: 'time-recording/view/time-recording-summary/actual',
          control: this.createValueModel()
        }),
        'totalExpectedHours': new FormFieldModel({
          key: 'totalExpectedHours', tags: ['app-expected-hours'],
          label: 'time-recording/view/time-recording-summary/expected',
          control: this.createValueModel()
        }),

        'totalSalariedHours': new FormFieldModel({
          key: 'totalSalariedHours', tags: ['app-salaried-hours'],
          label: 'time-recording/view/time-recording-summary/salaried',
          control: this.createValueModel(),
        }),
        'totalOvertime': new FormFieldModel({
          key: 'totalOvertime', tags: ['app-total-overtime'],
          label: 'time-recording/view/time-recording-summary/overtime',
          control: this.createValueModel()
        }),
        'totalOvertime50': new FormFieldModel({
          key: 'totalOvertime50', tags: ['app-total-overtime-50'],
          label: 'time-recording/view/time-recording-summary/overtime50',
          control: this.createValueModel()
        }),
        'totalOvertime100': new FormFieldModel({
          key: 'totalOvertime100', tags: ['app-total-overtime-100'],
          label: 'time-recording/view/time-recording-summary/overtime100',
          control: this.createValueModel()
        }),
        'totalHours': new FormFieldModel({
          key: 'totalHours', tags: ['app-total-hours'],
          label: 'time-recording/view/time-recording-summary/total-hours',
          control: this.createValueModel()
        }),
      })
    });
  }

  private refreshWorkingTimeModel(model: ContainerModel) {
    const summary = this.viewModel.summary;
    (model.items.get('totalWorkHours') as FormFieldModel).value = summary && summary.totalWorkHours;
    (model.items.get('totalPublicHolidayHours') as FormFieldModel).value = summary && summary.totalPublicHolidayHours;
    (model.items.get('totalSickHours') as FormFieldModel).value = summary && summary.totalSickHours;
    (model.items.get('totalShortTimeWorkHours') as FormFieldModel).value = summary && summary.totalShortTimeWorkHours;
    (model.items.get('totalHolidayHours') as FormFieldModel).value = summary && summary.totalHolidayHours;
    (model.items.get('totalCompTimeHours') as FormFieldModel).value = summary && summary.totalCompTimeHours;
    (model.items.get('totalActualHours') as FormFieldModel).value = summary && summary.totalActualHours;
    const totalExpectedHours = summary && summary.totalExpectedHours;
    (model.items.get('totalExpectedHours') as FormFieldModel).value = totalExpectedHours;
    const totalSalariedHours = summary && summary.totalSalariedHours;
    (model.items.get('totalSalariedHours') as FormFieldModel).value = totalSalariedHours;
    const isTotalSalariedHoursInvalid = Math.round(totalSalariedHours * 100) < Math.round((totalExpectedHours || 0) * 100);
    (model.items.get('totalSalariedHours') as FormFieldModel).tags.set('app-invalid-value', isTotalSalariedHoursInvalid);
    (model.items.get('totalOvertime') as FormFieldModel).value = summary && summary.totalOvertime;
    (model.items.get('totalOvertime50') as FormFieldModel).value = summary && summary.totalOvertime50;
    (model.items.get('totalOvertime100') as FormFieldModel).value = summary && summary.totalOvertime100;
    (model.items.get('totalHours') as FormFieldModel).value = summary && summary.totalHours;
  }

  private createFlexTimeModel(): ContainerModel {
    return new ContainerModel({
      items: new ObservableMap({
        'flextimeTitle': new TextModel({
          tags: ['app-flextime-title', 'app-title'],
          text: 'time-recording/view/time-recording-summary/flextime'
        }),
        'flextimeCarryover': new FormFieldModel({
          key: 'flextimeCarryover', tags: ['app-flextime-carryover'],
          label: 'time-recording/view/time-recording-summary/flextime-carryover',
          control: this.createValueModel()
        }),
        'compTime': new FormFieldModel({
          key: 'compTime', tags: ['app-comp-time'],
          label: 'time-recording/view/time-recording-summary/comp-time',
          control: this.createValueModel()
        }),
        'flextimeHours': new FormFieldModel({
          key: 'flextimeHours', tags: ['app-flextime'],
          label: 'time-recording/view/time-recording-summary/flextime-hours',
          control: this.createValueModel()
        }),
        'flextimeBalance': new FormFieldModel({
          key: 'flextimeBalance', tags: ['app-flextime-balance'],
          label: 'time-recording/view/time-recording-summary/flextime-balance',
          control: this.createValueModel()
        }),
      })
    });
  }

  private refreshFlexTimeModel(model: ContainerModel) {
    const summary = this.viewModel.summary;
    (model.items.get('flextimeCarryover') as FormFieldModel).value = summary && summary.flextimeCarryover;
    (model.items.get('compTime') as FormFieldModel).value = summary && hasValue(summary.totalCompTimeHours)
      ? -summary.totalCompTimeHours
      : null;
    (model.items.get('flextimeHours') as FormFieldModel).value = summary && summary.totalFlextime;
    (model.items.get('flextimeBalance') as FormFieldModel).value = summary && summary.flextimeBalance;
  }

  private createHolidayModel(): ContainerModel {
    return new ContainerModel({
      items: new ObservableMap({
        'holidayTitle': new TextModel({
          tags: ['app-holiday-title', 'app-title'],
          text: 'time-recording/view/time-recording-summary/holiday'
        }),
        'holidayCarryover': new FormFieldModel({
          key: 'holidayCarryover', tags: ['app-holiday-carryover'],
          label: 'time-recording/view/time-recording-summary/holiday-carryover',
          control: this.createValueModel()
        }),
        'holidayHours': new FormFieldModel({
          key: 'holidayHours', tags: ['app-holiday-hours'],
          label: 'time-recording/view/time-recording-summary/holiday-hours',
          control: this.createValueModel()
        }),
        'holidayBalance': new FormFieldModel({
          key: 'holidayBalance', tags: ['app-holiday-balance'],
          label: 'time-recording/view/time-recording-summary/holiday-balance',
          control: this.createValueModel()
        }),
      })
    });
  }

  private refreshHolidayModel(model: ContainerModel) {
    const summary = this.viewModel.summary;
    (model.items.get('holidayCarryover') as FormFieldModel).value = summary && summary.holidayCarryover;
    (model.items.get('holidayHours') as FormFieldModel).value = summary && hasValue(summary.totalHolidayHours)
      ? -summary.totalHolidayHours
      : null;
    (model.items.get('holidayBalance') as FormFieldModel).value = summary && summary.holidayBalance;
  }

  private createValueModel(data?: ValueData): ValueModel {
    return new ValueModel({
      tags: ['app-form-value'],
      format: 'number-2', suffix: ' h', alignment: Alignments.RIGHT,
      ...data
    });
  }

  private createProjectSumModel(): ContainerModel {
    return new ContainerModel({
      items: new ObservableMap({
          title: new TextModel({
            tags: ['app-title'],
            text: 'time-recording/view/time-recording-summary/projects'
          })
        }
      )
    });
  }

  private refreshProjectSumModel(model: ContainerModel) {
    const items = model.items;

    const timeRecordingService: TimeRecordingService = new TimeRecordingService();
    const projectSums: Map<string, number> = timeRecordingService.calculateProjectSums(this.viewModel.timeRecordList);
    const projectEntries = toArray(projectSums.entries()).map(value => {
      return {projectId: value[0], hours: value[1]};
    });
    const projectEntriesWithName = timeRecordingService.mapProjectNames(projectEntries, this.viewModel.projectList, true);
    for (let i = 0; i < projectEntriesWithName.length; i++) {
      const formFieldModel: FormFieldModel = new FormFieldModel({
        tags: ['multiline-kit-form-field-label'],
        label: projectEntriesWithName[i].projectName,
        control: this.createValueModel({value: projectEntriesWithName[i].hours})
      });
      if (items.size > i) {
        items.set(i.toString(), formFieldModel);
      }
    }
    while (items.size - 1 > projectEntriesWithName.length) {
      items.remove((items.size - 2).toString());
    }
  }
}
